import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { IAISystem } from 'modules/ai-inventory/ai-system/components/AISystemRecorder';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AISystemsService } from 'services/AISystemService';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  AppSelectWithKeys,
} from 'shared/components/AppComponents';
import LaunchIcon from '@mui/icons-material/Launch';
import { VulnerabilityService } from 'services/VulnerabilityService';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

interface ILinkedAISystems {
  linkedSystems: IAISystem[];
  vulnerabilityId: string;
  reloadDetail: () => void;
}

const LinkedAISystems: React.FC<ILinkedAISystems> = (props) => {
  const { linkedSystems, vulnerabilityId, reloadDetail } = props;
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [records, setRecords] = useState<IAISystem[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [isLinking, setIsLinking] = useState('');
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  // API Calls
  const loadAISystems = (): void => {
    setIsLoadingRecords(true);
    const queryString = new URLSearchParams({
      offset: '0',
      limit: '1000',
      search: '',
    }).toString();
    AISystemsService.getAISystems(queryString)
      .then((response) => {
        const { aiSystems } = response.data.data;
        /* eslint no-underscore-dangle: 0 */
        setRecords(aiSystems.map((d: any) => ({ ...d, id: d._id })));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  const linkRecord = (payload: any, callback: any): void => {
    VulnerabilityService.linkAISystem(vulnerabilityId, payload)
      .then((response) => {
        callback(response);
      })
      .catch(() => {
        callback(false);
      });
  };

  // UI Actions
  const actionOnLink = (status: string, id: string): void => {
    setIsLinking(status);
    linkRecord(
      {
        aisystemId: id,
        action: status,
      },
      (response: any) => {
        setIsLinking('');
        setSelectedRecordId('');
        if (typeof response === 'object') {
          reloadDetail();
        }
      }
    );
  };

  // Hooks
  useEffect(() => {
    loadAISystems();
  }, []);

  // UI Elements
  return (
    <Box className="app-plain-card">
      <Box className="title">
        <Stack spacing={2} direction="row" alignItems="center">
          <img height={20} src="/images/icons/IconLayer.svg" />
          <Typography variant="h4">AI Systems</Typography>
        </Stack>
        <Typography className="text-gray">
          AI systems affected by this vulnerability
        </Typography>
      </Box>

      <Box className="content">
        {isLoadingRecords && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}

        {!isLoadingRecords && (
          <>
            {orgACLConfig.vulnerabilities?.includes(
              'vulnerabilities.aiVulnerabilities.aisystem.link'
            ) && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1.5, sm: 4 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ width: '100%' }}
              >
                <AppSelectWithKeys
                  onChange={(e: any) => {
                    setSelectedRecordId(e.target.value);
                  }}
                  items={records.filter(
                    (r) => !linkedSystems.some((ls) => ls._id === r._id)
                  )}
                  titleKey="solutionName"
                  valueKey="_id"
                />
                <AppPrimaryButtonOutlined
                  isLoading={isLinking === 'link'}
                  disabled={!selectedRecordId}
                  title="Link"
                  onClick={() => {
                    actionOnLink('link', selectedRecordId);
                  }}
                />
              </Stack>
            )}
            {linkedSystems.length > 0 && (
              <List className="app-box-border mt-4 static-box">
                {linkedSystems.map((aiSystem, index) => (
                  <>
                    <ListItem>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={{ xs: 1.5, sm: 0 }}
                        sx={{ width: '100%' }}
                      >
                        <Stack>
                          <Link to={`/ai-systems/records/${aiSystem.id}`}>
                            <Stack
                              className="text-primary mb-2"
                              alignItems="center"
                              direction="row"
                              spacing={2}
                            >
                              <LaunchIcon />
                              <Typography className="f18 fw-6">
                                {aiSystem.solutionName}
                              </Typography>
                            </Stack>
                          </Link>
                          <Typography
                            variant="body1"
                            className="text-gray mb-2"
                          >
                            {aiSystem.businessGoals}
                          </Typography>
                          <Stack direction="row" spacing={2}>
                            <Chip
                              size="small"
                              label={`Risk: ${aiSystem.riskTier}`}
                            />
                            <Chip
                              size="small"
                              label={aiSystem.approvalStatus}
                            />
                            {aiSystem.lifecycleStage && (
                              <Chip
                                size="small"
                                label={`Risk: ${aiSystem.lifecycleStage}`}
                              />
                            )}
                          </Stack>
                        </Stack>
                        {orgACLConfig.vulnerabilities?.includes(
                          'vulnerabilities.aiVulnerabilities.aisystem.unlink'
                        ) && (
                          <AppPrimaryButton
                            isLoading={selectedRecordId === aiSystem._id}
                            onClick={() => {
                              setSelectedRecordId(aiSystem._id);
                              actionOnLink('unlink', aiSystem._id);
                            }}
                            className="btn-danger btn-extra-small"
                            title="Unlink"
                          />
                        )}
                      </Stack>
                    </ListItem>
                    {index < linkedSystems.length - 1 && <Divider />}
                  </>
                ))}
              </List>
            )}
            {linkedSystems.length === 0 && (
              <Box className="empty-content">
                <Typography className="f18">
                  No AI systems linked to this vulnerability
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default LinkedAISystems;
